import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, TextField, Chip, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress, Grid, Divider, Tooltip, Badge
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Done as DoneIcon,
  Person as PersonIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const PRIORITY_CONFIG = {
  normal:  { label: 'Normal',  sx: { bgcolor: '#e0e0e0', color: '#555' } },
  high:    { label: 'High',    sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  urgent:  { label: 'Urgent',  sx: { bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 } }
};

const STATUS_CONFIG = {
  pending:      { label: 'Pending',      sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  acknowledged: { label: 'Acknowledged', sx: { bgcolor: '#e3f2fd', color: '#1565c0' } },
  completed:    { label: 'Completed',    sx: { bgcolor: '#e8f5e9', color: '#2e7d32' } }
};

const today = () => new Date().toISOString().slice(0, 10);

export default function TeacherCareNotes() {
  const { user, logout } = useAuth();

  const [selectedDate, setSelectedDate] = useState(today());
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const [completeDialog, setCompleteDialog] = useState({ open: false, note: null });
  const [completionRemark, setCompletionRemark] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teacherAPI.careNotes.today(selectedDate);
      setNotes(res.data.notes || []);
    } catch {
      showSnack('Failed to load care notes', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAcknowledge = async (note) => {
    setActionLoading(note.id + '_ack');
    try {
      await teacherAPI.careNotes.acknowledge(note.id);
      showSnack('Note acknowledged');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to acknowledge', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteOpen = (note) => {
    setCompletionRemark('');
    setCompleteDialog({ open: true, note });
  };

  const handleCompleteSubmit = async () => {
    setActionLoading(completeDialog.note.id + '_complete');
    try {
      await teacherAPI.careNotes.complete(completeDialog.note.id, completionRemark);
      setCompleteDialog({ open: false, note: null });
      showSnack('Note marked as completed — parent notified');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to complete note', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredNotes = notes.filter(n => {
    if (filterStatus !== 'all' && n.status !== filterStatus) return false;
    if (filterPriority !== 'all' && n.priority !== filterPriority) return false;
    return true;
  });

  const stats = {
    total: notes.length,
    pending: notes.filter(n => n.status === 'pending').length,
    acknowledged: notes.filter(n => n.status === 'acknowledged').length,
    completed: notes.filter(n => n.status === 'completed').length
  };

  const borderColor = (note) => {
    if (note.priority === 'urgent') return '#c62828';
    if (note.priority === 'high') return '#e65100';
    return '#9e9e9e';
  };

  return (
    <>
    <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>Today's Care Notes</Typography>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label="Date"
            sx={{ width: 180 }}
          />
        </Box>

        {/* Stat strip */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total',      value: stats.total,       color: '#1976d2', bg: '#e3f2fd' },
            { label: 'Pending',    value: stats.pending,     color: '#e65100', bg: '#fff3e0' },
            { label: 'Acknowledged', value: stats.acknowledged, color: '#1565c0', bg: '#e8eaf6' },
            { label: 'Completed',  value: stats.completed,   color: '#2e7d32', bg: '#e8f5e9' }
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: s.bg }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: s.color }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Filter chips */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Status:</Typography>
            {['all', 'pending', 'acknowledged', 'completed'].map(s => (
              <Chip key={s} size="small"
                label={s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
                onClick={() => setFilterStatus(s)}
                variant={filterStatus === s ? 'filled' : 'outlined'}
                color={filterStatus === s ? 'primary' : 'default'}
              />
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Priority:</Typography>
            {['all', 'urgent', 'high', 'normal'].map(p => (
              <Chip key={p} size="small"
                label={p === 'all' ? 'All' : PRIORITY_CONFIG[p]?.label}
                onClick={() => setFilterPriority(p)}
                variant={filterPriority === p ? 'filled' : 'outlined'}
                color={filterPriority === p ? 'error' : 'default'}
              />
            ))}
          </Box>
        </Paper>

        {/* Notes list */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filteredNotes.length === 0 ? (
          notes.length === 0 ? (
            <EmptyState
              icon={AssignmentIcon}
              title="No care notes for this date — your students are all set!"
            />
          ) : (
            <EmptyState
              icon={AssignmentIcon}
              title="No notes match the current filter"
              variant="no-results"
            />
          )
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {filteredNotes.map(note => (
              <Card key={note.id} elevation={2} sx={{ borderRadius: 2, borderLeft: `4px solid ${borderColor(note)}` }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      {/* Student header */}
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }} flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={700}>
                          {note.student_name}
                        </Typography>
                        {note.admission_number && (
                          <Typography variant="caption" color="text.secondary">({note.admission_number})</Typography>
                        )}
                        <Typography variant="caption" sx={{ bgcolor: '#f3f4f6', px: 1, py: 0.2, borderRadius: 1, color: '#555' }}>
                          {note.class_name}{note.class_section ? ` — ${note.class_section}` : ''}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                        <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{note.parent_name}</Typography>
                        <EmailIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
                        <Typography variant="caption" color="text.secondary">{note.parent_email}</Typography>
                      </Box>

                      {/* Priority + status chips */}
                      <Box display="flex" gap={1} sx={{ mb: 1.5 }}>
                        <Chip size="small" label={PRIORITY_CONFIG[note.priority]?.label || note.priority}
                          sx={PRIORITY_CONFIG[note.priority]?.sx} />
                        <Chip size="small" label={STATUS_CONFIG[note.status]?.label || note.status}
                          sx={STATUS_CONFIG[note.status]?.sx} />
                      </Box>

                      {note.title && (
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{note.title}</Typography>
                      )}
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5 }}>
                        <Typography variant="body2">{note.note_text}</Typography>
                      </Paper>

                      {note.status === 'completed' && (
                        <Box sx={{ mt: 1 }}>
                          {note.completion_remark && (
                            <Box sx={{ p: 1, bgcolor: '#e8f5e9', borderRadius: 1, mb: 0.5 }}>
                              <Typography variant="caption" color="success.dark" fontWeight={600}>Your remark: </Typography>
                              <Typography variant="caption" color="success.dark">{note.completion_remark}</Typography>
                            </Box>
                          )}
                          {note.completed_at && (
                            <Typography variant="caption" color="text.secondary">
                              <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                              Completed {new Date(note.completed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { sm: 'flex-end' }, gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: { sm: 'right' } }}>
                        Sent {new Date(note.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      {note.status === 'pending' && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={actionLoading === note.id + '_ack' ? <CircularProgress size={14} /> : <VisibilityIcon />}
                          onClick={() => handleAcknowledge(note)}
                          disabled={!!actionLoading}
                          sx={{ minWidth: 130 }}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {(note.status === 'pending' || note.status === 'acknowledged') && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={actionLoading === note.id + '_complete' ? <CircularProgress size={14} color="inherit" /> : <DoneIcon />}
                          onClick={() => handleCompleteOpen(note)}
                          disabled={!!actionLoading}
                          sx={{ minWidth: 150 }}
                        >
                          Mark Completed
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>

      {/* Complete dialog */}
      <MobileFormDialog
        open={completeDialog.open}
        onClose={() => setCompleteDialog({ open: false, note: null })}
        title="Mark as Completed"
        actions={
          <>
            <Button onClick={() => setCompleteDialog({ open: false, note: null })}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleCompleteSubmit}
              disabled={!!actionLoading}
              startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            >
              Complete &amp; Notify Parent
            </Button>
          </>
        }
      >
        {completeDialog.note && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Note for {completeDialog.note.student_name}:</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{completeDialog.note.note_text}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Completion remark (optional)"
          placeholder="E.g., Gave cough syrup at 11 AM and 4 PM"
          value={completionRemark}
          onChange={e => setCompletionRemark(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The parent will receive an email and in-app notification when you mark this as completed.
        </Typography>
      </MobileFormDialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
}
